/**
 * COMPTABILITÉ — CA ANNUEL, MOIS PAR MOIS.
 *
 * ═══ UN MODÈLE DE LECTURE, ET UNE SEULE ÉCRITURE POSSIBLE ═══
 *
 * Rien ne se saisit sur cette page. Le chiffre d'affaires vient des clôtures
 * finalisées et des nuits VALIDÉES SANS CLÔTURE DÉTAILLÉE, point. La seule
 * écriture offerte ici est un AJUSTEMENT : un montant motivé, tracé, qui
 * s'ajoute au calcul SANS l'écraser — et l'écran montre alors chaque ligne :
 *
 *     Calculé                          142 380,00 €
 *     Validé sans clôture détaillée     21 771,00 €
 *     Ajustements                         + 13,50 €
 *     Retenu                           164 164,50 €
 *
 * Une nuit validée sans clôture détaillée (une nuit d'avant Gestion, ou dont
 * la clôture a été ignorée) est un chiffre arrêté EN BLOC : il compte dans le
 * retenu du mois, de l'année et de l'exercice — sinon ces nuits
 * disparaîtraient du chiffre d'affaires —, mais toujours NOMMÉ à part, jamais
 * fondu dans le calculé, qui reste la somme des composantes des clôtures. La
 * table le porte dans sa propre colonne, entre Calculé et Ajustements : on
 * refait l'addition ligne à ligne, à l'écran. Un mois peut n'avoir QUE des
 * nuits validées — il a un chiffre arrêté, simplement sans composantes.
 *
 * La table d'ajustements est en ÉCRITURE SEULE côté base : on ne corrige pas
 * une correction, on en pose une nouvelle, et les deux restent lisibles. C'est
 * plus lourd, et c'est exactement ce qu'on veut d'une correction de chiffre
 * d'affaires. Cet écran les liste donc toutes, avec leur auteur et leur motif.
 *
 * ═══ DEUX AGRÉGATS QU'ON NE CONFOND PAS ═══
 *
 * L'ANNÉE CIVILE (janvier → décembre) et l'EXERCICE (la saison, quand elle est
 * configurée) sont deux totaux différents, affichés séparément. Tant que
 * l'exercice n'est pas configuré, la page le DIT — elle n'invente aucune date
 * de début de saison, parce qu'un défaut plausible aurait produit un total
 * d'apparence officielle que personne n'a validé.
 *
 * ═══ CE QUI N'EXISTE PAS NE S'AFFICHE PAS ═══
 *
 * Les colonnes sont déduites des mois réellement alimentés, et la comparaison
 * à l'année précédente n'apparaît que si le serveur a jugé qu'elle avait un
 * sens ET qu'un montant de référence existe. Ses RÉSERVES (« l'année comparée
 * n'est clôturée qu'à moitié ») s'affichent avec elle : une comparaison sans
 * ses réserves se lit comme un effondrement du chiffre d'affaires, alors qu'il
 * s'agit d'un retard de saisie.
 *
 * ROUTES SERVIES (`apps/api/src/routes/finance-revenue.ts`) :
 *   GET  /api/v1/finance/revenue/annual?year        finance.revenue.read
 *   GET  /api/v1/finance/revenue/months?year        finance.revenue.read  (repli)
 *   POST /api/v1/finance/revenue/adjustments        finance.revenue.adjust (step-up)
 *        { scope: 'MOIS' | 'ANNEE', period_start, period_end, component_code, amount_cents, reason }
 */

import { useEffect, useMemo, useState } from 'react'
import { CalendarRange, ChevronLeft, ChevronRight, Info, Plus } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, Select, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { formatMoney, parseMoney } from './money.js'
import { remplacerRecherche } from './adresse-ecran.js'
import {
  Comparaison, EtatBloc, LIBELLE_VALIDE, MotifChamp, TroisLignes,
  colonnesActives, dernierJour, libelleJour, libelleMois, lireAnnuel, lireMois,
  montantDe, motifValide, nuitsTexte, premierJour, totalChamp, totalColonne, useRessource,
  ymdAujourdhui,
  type Colonne, type MoisCA,
} from './RevenueShared.js'
import './finance.css'
import './finance-revenue.css'

/* ═══════════════════════ création d'un ajustement ═══════════════════════ */

type Portee = 'MOIS' | 'ANNEE'

function ModaleAjustement(props: {
  annee: number
  mois: string | null
  colonnes: Colonne[]
  onFermer: () => void
  onCree: () => void
}) {
  const toast = useToast()
  const [portee, setPortee] = useState<Portee>(props.mois === null ? 'ANNEE' : 'MOIS')
  const [mois, setMois] = useState(props.mois ?? `${props.annee}-01`)
  const [composante, setComposante] = useState('')
  const [montant, setMontant] = useState('')
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const cents = parseMoney(montant)
  const pret = cents !== null && cents !== 0 && motifValide(motif) && !envoi

  const moisPossibles = Array.from({ length: 12 }, (_, i) => {
    const code = `${props.annee}-${String(i + 1).padStart(2, '0')}`
    return { value: code, label: libelleMois(code) }
  })

  // La période est TOUJOURS explicite : le serveur ne devine pas ce que « le
  // mois de mars » veut dire, et deux écrans qui le devineraient différemment
  // finiraient par ajuster deux fenêtres différentes.
  const debut = portee === 'MOIS' ? premierJour(mois) : `${props.annee}-01-01`
  const fin = portee === 'MOIS' ? dernierJour(mois) : `${props.annee}-12-31`

  const envoyer = async () => {
    if (cents === null) return
    setEnvoi(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', '/api/v1/finance/revenue/adjustments',
      {
        scope: portee,
        period_start: debut,
        period_end: fin,
        component_code: composante === '' ? null : composante,
        amount_cents: cents,
        reason: motif.trim(),
      },
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', `Ajustement de ${formatMoney(cents, { signe: true })} enregistré.`)
      props.onCree()
      props.onFermer()
      return
    }
    toast('error', r.data.error === 'NOT_FOUND'
      ? 'La création d’ajustement n’est pas servie par l’API : rien n’a été enregistré.'
      : (r.data.message ?? 'L’ajustement a été refusé par le serveur.'))
  }

  return (
    <Modal open onClose={props.onFermer} owning ariaLabel="Créer un ajustement" width={640}>
      <header className="lc-modal__head">
        <div>
          <div className="lc-modal__title">Créer un ajustement</div>
          <div className="fr-mini">
            Le calcul issu des clôtures n’est pas modifié : l’ajustement s’ajoute, et
            les trois lignes restent lisibles.
          </div>
        </div>
      </header>
      <div className="lc-modal__body">
        <div className="fr-fields">
          <Field label="Portée">
            <Select
              value={portee}
              options={[{ value: 'MOIS', label: 'Un mois' }, { value: 'ANNEE', label: `L’année ${props.annee}` }]}
              onChange={(v) => setPortee(v === 'ANNEE' ? 'ANNEE' : 'MOIS')}
              ariaLabel="Portée de l’ajustement"
            />
          </Field>
          {portee === 'MOIS' ? (
            <Field label="Mois concerné">
              <Select value={mois} options={moisPossibles} onChange={setMois} ariaLabel="Mois concerné" />
            </Field>
          ) : null}
          <Field label="Composante" helper="Facultatif. Sans composante, l’ajustement porte sur le total de la période.">
            <Select
              value={composante}
              options={[{ value: '', label: 'Aucune — total de la période' }, ...props.colonnes.map((c) => ({ value: c.code, label: c.libelle }))]}
              onChange={setComposante}
              ariaLabel="Composante concernée"
            />
          </Field>
          <Field
            label="Montant"
            helper={cents === null
              ? 'Un montant en euros, négatif si l’ajustement retranche. Exemple : -13,50'
              : `Sera enregistré : ${formatMoney(cents, { signe: true })}`}
          >
            <TextInput value={montant} onChange={setMontant} placeholder="13,50" ariaLabel="Montant de l’ajustement" />
          </Field>
          <MotifChamp
            valeur={motif} onChange={setMotif} label="Motif"
            aide="Ce motif est conservé avec votre nom : c’est ce que lira la personne qui reprendra ce chiffre dans six mois."
          />
          <p className="fr-mini">
            Période visée : du {libelleJour(debut)} au {libelleJour(fin)}.
          </p>
        </div>
        <div className="fr-actions fr-actions--end">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!pret} onClick={() => { void envoyer() }}>
            {envoi ? 'Enregistrement en cours' : 'Créer l’ajustement'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════════ la page ═══════════════════════════════ */

/**
 * L'ANNÉE VIT DANS L'ADRESSE (`?annee=2025`). En mémoire seulement, elle
 * revenait à l'année courante au premier Retour : on consultait 2025, on
 * descendait dans un mois, on revenait… sur 2026. Une année hors de portée
 * raisonnable est ignorée plutôt que de déclencher une lecture absurde.
 */
function anneeDepuisUrl(): number {
  const brut = new URLSearchParams(window.location.search).get('annee')
  const n = brut !== null && /^\d{4}$/.test(brut) ? Number(brut) : NaN
  return n >= 2000 && n <= 2100 ? n : Number(ymdAujourdhui().slice(0, 4))
}

export function FinanceAnnualPage() {
  const [annee, setAnnee] = useState(anneeDepuisUrl)
  const [modale, setModale] = useState<{ mois: string | null } | null>(null)
  const { permissions, loading } = usePermissions()

  const annuel = useRessource(`/api/v1/finance/revenue/annual?year=${annee}`, lireAnnuel)
  // Repli : si l'exercice complet n'est pas lisible, les mois seuls suffisent
  // à afficher quelque chose de VRAI — sans totaux d'exercice.
  const repli = useRessource(
    annuel.etat.phase === 'absente' ? `/api/v1/finance/revenue/months?year=${annee}` : null,
    lireMois,
  )

  const donnees = annuel.etat.phase === 'ok' ? annuel.etat.data : null
  const moisReplis: MoisCA[] = repli.etat.phase === 'ok' ? repli.etat.data : []
  const mois = donnees?.mois ?? moisReplis
  const colonnes = useMemo(() => colonnesActives(mois), [mois])
  const comparaison = donnees?.comparaison ?? null
  const precedent = comparaison?.mois ?? []
  // Doctrine : une colonne que personne n'alimente sur la période n'est pas
  // une information, c'est du bruit. Elle revient d'elle-même au premier chiffre.
  const colonneNuits = mois.some((m) => m.nuits !== null || m.nuitsValidees !== null)
  // « Validé sans clôture détaillée » est un terme de l'ADDITION, au même
  // titre que Calculé et Ajustements : la colonne est là dès que le serveur la
  // sert — y compris à « 0,00 € », un zéro MESURÉ (aucune nuit validée ce
  // mois-là), pas inventé. Sans elle, Calculé + Ajustements ne ferait plus
  // Retenu sous les yeux du lecteur. Non servie, elle se tait, et une cellule
  // inconnue s'écrit « — ».
  const colonneValide = mois.some((m) => m.valide !== null)
  const peutAjuster = !loading && permissions.includes('finance.revenue.adjust')

  // Changer d'année n'est pas changer de page : l'entrée est REMPLACÉE.
  useEffect(() => {
    remplacerRecherche('/comptabilite/chiffre-affaires/annuel', new URLSearchParams({ annee: String(annee) }))
  }, [annee])

  const refDe = (ym: string) => precedent.find((m) => m.mois.slice(5) === ym.slice(5))
  const ouvrirMois = (ym: string) => navigate(`/comptabilite/chiffre-affaires?vue=mois&mois=${ym}`)

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><CalendarRange size={26} aria-hidden="true" /> CA annuel</h1>
          <p className="lc-subtitle">
            L’année mois par mois, telle que les clôtures l’ont produite — les nuits
            validées sans clôture détaillée comptées à part. Aucune saisie directe : une
            correction est un ajustement motivé, visible à côté du calcul.
          </p>
        </div>
        <div className="fr-hero__actions">
          <div className="fr-step">
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Année précédente"
              onClick={() => setAnnee((a) => a - 1)}><ChevronLeft size={16} /></button>
            <span className="fr-step__label">{annee}</span>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Année suivante"
              onClick={() => setAnnee((a) => a + 1)}><ChevronRight size={16} /></button>
          </div>
          {peutAjuster ? (
            <button type="button" className="lc-btn" onClick={() => setModale({ mois: null })}>
              <Plus size={15} aria-hidden="true" /> Ajustement
            </button>
          ) : null}
        </div>
      </header>

      <EtatBloc etat={annuel.etat.phase === 'absente' ? repli.etat : annuel.etat} quoi="l’exercice" />

      {donnees !== null ? (
        <>
          <div className="fr-kpis">
            <div className="fr-kpi fr-kpi--accent">
              <span className="fi-kpi__label">Retenu — année civile {annee}</span>
              <span className="fi-kpi__value">{formatMoney(donnees.totaux.retenu)}</span>
              <span className="fr-kpi__foot">
                {nuitsTexte(donnees.nuits, donnees.nuitsValidees) ?? 'Du 1ᵉʳ janvier au 31 décembre'}.
              </span>
            </div>
            <div className="fr-kpi">
              <span className="fi-kpi__label">Exercice comptable</span>
              <span className="fi-kpi__value">
                {donnees.saison === null
                  ? <span className="fr-mini">Saison à configurer</span>
                  : formatMoney(donnees.saison.totaux.retenu)}
              </span>
              <span className="fr-kpi__foot">
                {donnees.saison === null
                  ? <>Gestion ne devine pas la date d’ouverture — <Link className="lc-link" href="/comptabilite/regles">la définir</Link>.</>
                  : <>Du {libelleJour(donnees.saison.debut)} au {libelleJour(donnees.saison.fin)}.</>}
              </span>
            </div>
            <div className="fr-kpi">
              <span className="fi-kpi__label">Année précédente</span>
              <span className="fi-kpi__value">
                {comparaison !== null
                  ? formatMoney(comparaison.precedent)
                  : <span className="fr-mini">Pas d’historique</span>}
              </span>
              <span className="fr-kpi__foot">
                {comparaison !== null
                  ? <Comparaison actuel={comparaison.actuel} reference={comparaison.precedent} quoi={`vs. ${comparaison.anneePrecedente ?? 'l’an dernier'}`} />
                  : 'Aucune donnée comparable : la comparaison est masquée plutôt qu’affichée à zéro.'}
              </span>
            </div>
          </div>

          {comparaison !== null && comparaison.reserves.length > 0 ? (
            <div className="fr-reserves">
              {comparaison.reserves.map((r) => <p key={r} className="fi-reserve">{r}</p>)}
            </div>
          ) : null}

          <TroisLignes
            calcule={donnees.totaux.calcule}
            valide={donnees.totaux.valide}
            nuitsValidees={donnees.nuitsValidees}
            ajustements={donnees.totaux.ajustements}
            retenu={donnees.totaux.retenu}
            note="Le calculé vient des clôtures finalisées ; le validé sans clôture détaillée, des nuits arrêtées en bloc, sans composantes ; les ajustements sont des corrections motivées et tracées — jamais une réécriture du détail."
          />
        </>
      ) : null}

      {donnees === null && moisReplis.length > 0 ? (
        <div className="fr-note">
          <Info size={16} aria-hidden="true" />
          <span>
            <strong>Vue partielle.</strong> Les mois sont lus, mais pas le total de
            l’exercice : le tableau ci-dessous ne totalise que les mois affichés, et
            aucun cumul de saison n’est affirmé.
          </span>
        </div>
      ) : null}

      <section className="fr-section">
        <div className="fr-section__head">
          <h2 className="fr-section__title">Détail par mois</h2>
          <span className="fr-section__hint">
            {colonnes.length > 0
              ? `${colonnes.length} catégorie${colonnes.length > 1 ? 's' : ''} alimentée${colonnes.length > 1 ? 's' : ''} sur l’année`
              : 'Aucune catégorie alimentée'}
          </span>
        </div>

        {mois.length === 0 ? (
          <div className="fi-empty">
            <div className="fi-empty__title">Aucun mois alimenté en {annee}</div>
            Aucune clôture finalisée n’a produit de recette sur cette année, et aucune
            nuit n’y a été validée sans clôture détaillée. Une page vide vaut mieux
            qu’une colonne de zéros que personne n’a constatés.
          </div>
        ) : (
          <div className="fi-tablewrap">
            <table className="fi-table">
              <thead>
                <tr>
                  <th scope="col">Mois</th>
                  {colonneNuits ? <th scope="col">Nuits</th> : null}
                  {colonnes.map((c) => <th key={c.code} scope="col" className="fi-money">{c.libelle}</th>)}
                  <th scope="col" className="fi-money">Calculé</th>
                  {colonneValide ? <th scope="col" className="fi-money fr-th-long">{LIBELLE_VALIDE}</th> : null}
                  <th scope="col" className="fi-money">Ajustements</th>
                  <th scope="col" className="fi-money">Retenu</th>
                  {precedent.length > 0 ? <th scope="col">N-1</th> : null}
                  {peutAjuster ? <th scope="col"><span className="u-sr">Actions</span></th> : null}
                </tr>
              </thead>
              <tbody>
                {mois.map((m) => (
                  <tr
                    key={m.mois} className="fr-row" tabIndex={0} role="button"
                    aria-label={`Voir les nuits de ${libelleMois(m.mois)}`}
                    onClick={() => ouvrirMois(m.mois)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrirMois(m.mois) } }}
                  >
                    <td className="fr-cell-main">{libelleMois(m.mois)}</td>
                    {colonneNuits ? <td>{nuitsTexte(m.nuits, m.nuitsValidees) ?? <span className="fr-mini">—</span>}</td> : null}
                    {colonnes.map((c) => (
                      <td key={c.code} className="fi-money">{formatMoney(montantDe(m.composantes, c.code))}</td>
                    ))}
                    <td className="fi-money fi-money--faible">{formatMoney(m.calcule)}</td>
                    {colonneValide ? <td className="fi-money">{formatMoney(m.valide)}</td> : null}
                    <td className="fi-money">{formatMoney(m.ajustements, { signe: true })}</td>
                    <td className="fi-money fi-money--fort">{formatMoney(m.retenu)}</td>
                    {precedent.length > 0 ? (
                      <td><Comparaison actuel={m.retenu} reference={refDe(m.mois)?.retenu ?? null} /></td>
                    ) : null}
                    {peutAjuster ? (
                      <td>
                        <button
                          type="button" className="lc-btn lc-btn--ghost lc-btn--sm"
                          aria-label={`Créer un ajustement sur ${libelleMois(m.mois)}`}
                          onClick={(e) => { e.stopPropagation(); setModale({ mois: m.mois }) }}
                        >
                          Ajuster
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={colonneNuits ? 2 : 1}>Total des mois affichés</td>
                  {colonnes.map((c) => (
                    <td key={c.code} className="fi-money">{formatMoney(totalColonne(mois, c.code))}</td>
                  ))}
                  <td className="fi-money">{formatMoney(totalChamp(mois, (m) => m.calcule))}</td>
                  {colonneValide ? <td className="fi-money">{formatMoney(totalChamp(mois, (m) => m.valide))}</td> : null}
                  <td className="fi-money">{formatMoney(totalChamp(mois, (m) => m.ajustements), { signe: true })}</td>
                  <td className="fi-money">{formatMoney(totalChamp(mois, (m) => m.retenu))}</td>
                  {precedent.length > 0 ? <td /> : null}
                  {peutAjuster ? <td /> : null}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="fr-mini">
          Une ligne mène aux nuits du mois.
          {colonneValide ? <> Sur chaque ligne, Calculé + {LIBELLE_VALIDE} + Ajustements = Retenu.</> : null}
          {' '}Le total ci-dessus additionne les mois affichés — il ne prétend pas être
          celui de l’exercice, qui a ses propres bornes.
        </p>
      </section>

      {donnees !== null && donnees.ajustements.length > 0 ? (
        <section className="fr-section">
          <div className="fr-section__head">
            <h2 className="fr-section__title">Ajustements de l’année</h2>
            <span className="fr-section__hint">
              {donnees.ajustements.length} correction{donnees.ajustements.length > 1 ? 's' : ''} — aucune n’est modifiable
            </span>
          </div>
          <div className="fi-tablewrap">
            <table className="fi-table">
              <thead>
                <tr>
                  <th scope="col">Période</th>
                  <th scope="col">Composante</th>
                  <th scope="col" className="fi-money">Montant</th>
                  <th scope="col">Motif</th>
                  <th scope="col">Auteur</th>
                </tr>
              </thead>
              <tbody>
                {donnees.ajustements.map((a) => (
                  <tr key={a.id}>
                    <td className="fr-cell-main">
                      {a.debut !== null ? libelleJour(a.debut) : '—'}
                      {a.fin !== null ? <small className="fr-cell-sub">jusqu’au {libelleJour(a.fin)}</small> : null}
                    </td>
                    <td>{a.composante ?? <span className="fr-mini">Total de la période</span>}</td>
                    <td className="fi-money">{formatMoney(a.cents, { signe: true })}</td>
                    <td>{a.motif}</td>
                    <td>{a.auteur ?? <span className="fr-mini">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fr-mini">
            Un ajustement ne se modifie ni ne s’efface : pour en corriger un, on en pose
            un autre, et les deux restent lisibles.
          </p>
        </section>
      ) : null}

      {modale !== null ? (
        <ModaleAjustement
          annee={annee}
          mois={modale.mois}
          colonnes={colonnes}
          onFermer={() => setModale(null)}
          onCree={() => { annuel.recharger(); repli.recharger() }}
        />
      ) : null}
    </main>
  )
}
